import React from 'react';
import { Card, Breadcrumb, Space, Button, Tag } from 'antd';
import { ArrowLeftOutlined, SaveOutlined, CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

interface BreadcrumbItem {
    title: string;
    path?: string;
}

interface FormPageLayoutProps {
    title: string;
    breadcrumbs: BreadcrumbItem[];
    /** Auto-save status */
    saveStatus?: 'idle' | 'saving' | 'saved' | 'error' | 'hasChanges';
    /** Show manual save button */
    showSaveButton?: boolean;
    /** Manual save handler */
    onSave?: () => void;
    /** Save button loading state */
    saving?: boolean;
    children: React.ReactNode;
}

export const FormPageLayout: React.FC<FormPageLayoutProps> = ({
    title,
    breadcrumbs,
    saveStatus = 'idle',
    showSaveButton = false,
    onSave,
    saving = false,
    children
}) => {
    const navigate = useNavigate();

    const renderSaveStatus = () => {
        switch (saveStatus) {
            case 'saving':
                return <Tag icon={<ClockCircleOutlined spin />} color="processing">保存中...</Tag>;
            case 'saved':
                return <Tag icon={<CheckCircleOutlined />} color="success">已保存</Tag>;
            case 'error':
                return <Tag icon={<ExclamationCircleOutlined />} color="error">保存失败</Tag>;
            case 'hasChanges':
                return <Tag color="warning">有未保存更改</Tag>;
            default:
                return null;
        }
    };

    return (
        <div style={{ padding: '0 0 24px 0' }}>
            {/* Header with Breadcrumb */}
            <div style={{
                background: '#fff',
                padding: '16px 24px',
                marginBottom: 16,
                borderBottom: '1px solid #f0f0f0',
                position: 'sticky',
                top: 0,
                zIndex: 100
            }}>
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <Breadcrumb>
                        {breadcrumbs.map((item, index) => (
                            <Breadcrumb.Item key={index}>
                                {item.path ? (
                                    <a onClick={() => navigate(item.path!)}>{item.title}</a>
                                ) : (
                                    <span>{item.title}</span>
                                )}
                            </Breadcrumb.Item>
                        ))}
                    </Breadcrumb>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Space>
                            <Button
                                icon={<ArrowLeftOutlined />}
                                onClick={() => navigate(-1)}
                            >
                                返回
                            </Button>
                            <h2 style={{ margin: 0 }}>{title}</h2>
                        </Space>

                        <Space>
                            {renderSaveStatus()}
                            {showSaveButton && (
                                <Button
                                    type="primary"
                                    icon={<SaveOutlined />}
                                    onClick={onSave}
                                    loading={saving}
                                >
                                    保存
                                </Button>
                            )}
                        </Space>
                    </div>
                </Space>
            </div>

            {/* Form Content */}
            <div style={{ padding: '0 24px' }}>
                <Card>
                    {children}
                </Card>
            </div>
        </div>
    );
};
